import { useAppDispatch, useAppSelector } from 'src/hooks/store';
import { Button, Spacer, Switch } from '@odekoteam/doppio';
import CancelButton from '../CancelButton';
import { Flex } from 'theme-ui';
import { useEffect, useState } from 'react';
import { modalSelector } from 'src/state/selectors/modal';
import { GetLocation } from 'src/graphql/types/delivery';
import { useMutation } from 'urql';
import { hideModal } from 'src/state/slices/modal';
import { LoaderSpinner } from '../../Loader';
import { updateRoutingLocations } from 'src/state/slices/locations';
import ExpandableLocationsList from './ExpandableLocationsList';
import { UPDATE_LOCATIONS_DELIVER_WITHIN_STORE_HOURS } from 'src/graphql/requests/updateLocationsDeliverWithinStoreHours';
import { getScheduledDeliveryTimeSource } from 'src/utils/locations';

const DeliverWithinStoreHoursTab = (): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const {
    variables: { locations },
  } = useAppSelector(modalSelector);
  const [deliverWithinStoreHours, setDeliverWithinStoreHours] =
    useState<boolean>(true);
  const getLabel = (): string => (deliverWithinStoreHours ? 'ON' : 'OFF');
  const [
    updateLocationsDeliverWithinStoreHoursResult,
    updateLocationsDeliverWithinStoreHours,
  ] = useMutation(UPDATE_LOCATIONS_DELIVER_WITHIN_STORE_HOURS);

  const handleAction = (): void => {
    const locationIds = locations.map(
      (location: GetLocation): string => location.id,
    );
    updateLocationsDeliverWithinStoreHours({
      input: {
        locationIds,
        deliverWithinStoreHours,
      },
    });
  };

  useEffect(() => {
    if (updateLocationsDeliverWithinStoreHoursResult.data) {
      const routingLocations = locations.map((location: GetLocation) => ({
        locationId: location.id,
        deliverWithinStoreHours,
        scheduledDeliveryTimeSource: getScheduledDeliveryTimeSource(
          deliverWithinStoreHours,
          location.storeHoursConfirmed,
        ),
        storeHoursConfirmed: location.storeHoursConfirmed,
      }));
      dispatch(updateRoutingLocations(routingLocations));
      dispatch(hideModal());
    }
  }, [
    updateLocationsDeliverWithinStoreHoursResult,
    deliverWithinStoreHours,
    dispatch,
    locations,
  ]);

  return (
    <>
      <Flex sx={{ alignItems: 'center', marginBottom: '10px' }}>
        <span sx={{ variant: 'text.paragraphMdDefault' }}>
          Update deliver within store hours to:
        </span>
        <div sx={{ marginX: '8px' }}>
          <Switch
            id="deliver-within-store-hours-switch-bulk"
            accessibilityLabel="deliver within store hours toggle"
            onCheckedChange={(): void =>
              setDeliverWithinStoreHours(!deliverWithinStoreHours)
            }
            size="default"
            isChecked={deliverWithinStoreHours}
          />
        </div>
        <span sx={{ variant: 'text.paragraphLgDefault' }}>{getLabel()}</span>
      </Flex>
      <div sx={{ variant: 'text.paragraphMdDefault', marginBottom: '20px' }}>
        You will be updating {locations.length} locations. Would you like to
        turn deliver within store hours{' '}
        <b sx={{ variant: 'text.labelMd' }}>{getLabel()}</b> for these{' '}
        {locations.length} locations?
      </div>
      <ExpandableLocationsList locations={locations} />
      <Flex sx={{ justifyContent: 'center', marginTop: '24px' }}>
        {updateLocationsDeliverWithinStoreHoursResult.fetching && (
          <LoaderSpinner
            imgStyle={{
              marginLeft: '-200px',
              bottom: '32px',
              height: '20px',
              top: 'auto',
              left: 'auto',
            }}
          />
        )}
        <Button
          id="modal-update-deliver-within-store-hours-action-button"
          variant="secondary"
          onPress={handleAction}
          isDisabled={updateLocationsDeliverWithinStoreHoursResult.fetching}
        >
          Update
        </Button>
        <Spacer width="$1" />
        <CancelButton />
      </Flex>
    </>
  );
};
export default DeliverWithinStoreHoursTab;
